import React, { useContext, useMemo, useRef } from "react"
import { Form } from "react-bootstrap"
import { get, useFormContext, useFormState } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useAutoFocus } from "shared/lib/useAutoFocus"
import { useMergedRefs } from "shared/lib/useMergedRefs"

import { Input as DefaultInput, InputContext } from "../../Input"
import { useInputDescriptionIDs } from "../../accessibility/useInputAriaDescription"
import { BooleanInputContext, BooleanInputRootProps } from "../BooleanInput"
import { ReadonlyBoolean } from "../components/ReadonlyBoolean"
import "./styles/_toggle.scss"

const Root = React.memo(
  ({ trueLabel, falseLabel, className, ...props }: BooleanInputRootProps) => {
    const { t } = useTranslation()
    const context = useMemo(
      () => ({
        trueLabel: trueLabel ?? t("Boolean.true"),
        falseLabel: falseLabel ?? t("Boolean.false")
      }),
      [trueLabel, falseLabel]
    )
    return (
      <BooleanInputContext.Provider value={context}>
        <DefaultInput.Root
          {...props}
          className={classNames("toggle-input", className)}
        />
      </BooleanInputContext.Provider>
    )
  }
)

const Input = React.memo(
  React.forwardRef(
    (
      props: ToggleProps,
      externalRef?: React.ForwardedRef<HTMLInputElement>
    ) => {
      const { name } = useContext(InputContext)

      if (name) {
        return <HookFormInput {...props} ref={externalRef} />
      }
      return <ToggleComponent {...props} ref={externalRef} />
    }
  )
)

const HookFormInput = React.memo(
  React.forwardRef(
    (
      { className, ...props }: ToggleProps,
      externalRef?: React.ForwardedRef<HTMLInputElement>
    ) => {
      const { name, required, disabled } = useContext(InputContext)
      const { register } = useFormContext()
      const { errors } = useFormState({ name })
      const error = get(errors, name)

      const registerProps = register(name ?? "", { required, disabled })

      const ref = useMergedRefs(externalRef, registerProps.ref)

      return (
        <ToggleComponent
          {...registerProps}
          {...props}
          className={classNames(className, { error })}
          ref={ref}
        />
      )
    }
  )
)

export interface ToggleProps extends React.ComponentProps<typeof Form.Check> {}

const ToggleComponent = React.forwardRef(
  (
    { className, type, ...rest }: React.ComponentProps<typeof Form.Check>,
    externalRef?: React.ForwardedRef<HTMLInputElement>
  ) => {
    const internalRef = useRef<HTMLInputElement>(null)
    const { id, readonly, disabled, loading, autoFocus, label } =
      useContext(InputContext)

    const { ariaDescribedBy } = useInputDescriptionIDs({
      additionalDescriptions: rest["aria-describedby"]
    })

    useAutoFocus({ autoFocus, ref: internalRef })

    const ref = useMergedRefs(externalRef, internalRef)

    if (readonly) {
      return null
    }

    return (
      <Form.Check
        {...rest}
        id={id}
        type="switch"
        // Falls back to undefined so a missing label leaves the visible
        // <label for> as the accessible name instead of overriding it with an
        // empty aria-label.
        aria-label={label || undefined}
        disabled={disabled || loading}
        aria-describedby={ariaDescribedBy}
        ref={ref}
        className={classNames(
          "input__control",
          "toggle",
          { disabled },
          className
        )}
      />
    )
  }
)

const Toggle = {
  ...DefaultInput,
  Root,
  Input,
  Readonly: ReadonlyBoolean
}

export { Toggle }
